import type { RawLocation } from "vue-router";

import { useRouter, useContext } from "@nuxtjs/composition-api";
import { useUser } from "~/modules/customer/composables/useUser";
import { useCart } from "~/modules/checkout/composables/useCart";
import { useConfigStore, useVoucherStore } from "~/stores/extensionApi";
import { computed } from "@vue/composition-api";
import { useUiState } from "~/composables/useUiState";
import { useOrderStore } from "~/stores/extensionApi/order";
import { formatDateTime, isEmail } from "~/helpers";

type LinkGroup = { title?: string; items: LinkGroupItem[] };
type LinkGroupItem = {
  id: string;
  label: string;
  des?: string;
  link?: RawLocation;
  class?: string;
  message?: string;
  count?: number;
  classStatus?: string;
};

export const useSidebarLinkGroups = () => {
  const { localeRoute } = useContext();
  const { logout, user: userCurrent } = useUser();
  const { clear } = useCart();
  const orderStore = useOrderStore();
  const listOrder = computed(() => orderStore.listOrder?.order || []);
  const listStatus = ["Created", "Validated", "To be Prepared", "Prepared", "Pending"];
  const currentOrder = computed(() => {
    return listOrder.value?.find((item) => listStatus.includes(item?.status)) || null;
  });

  const storeConfig = useConfigStore();
  const defaultDob = computed(() => formatDateTime(storeConfig?.getOtpConfig?.default_dob, "DD/MM/YYYY"));
  const isUpdateProfile = computed(() => {
    if (!userCurrent.value) return false;
    const isName = userCurrent.value?.firstname && userCurrent.value.firstname !== "-";
    const isGender = userCurrent.value?.gender && (userCurrent.value.gender == 1 || userCurrent.value.gender == 2);
    const hasEmail = userCurrent.value?.email && isEmail(userCurrent.value.email);
    const isPhone = userCurrent.value?.phone_number && userCurrent.value.phone_number !== "-";
    const isBirthDay = userCurrent.value?.date_of_birth && formatDateTime(userCurrent.value.date_of_birth, "DD/MM/YYYY") !== defaultDob.value;
    return !(isName && isGender && hasEmail && isPhone && isBirthDay);
  });

  const router = useRouter();
  const voucherStore = useVoucherStore();
  const voucherData = computed(() => voucherStore.getVoucherData);
  const { toggleAccounMobile } = useUiState();
  // Define pages that require authentication
  const authRequiredPages = [
    "my-profile",
    "order-history",
    "membership-card",
    // 'my-wishlist',
    "address-details",
    "my-coupon",
  ];

  const orderStatusMessage = computed(() => {
    if (!currentOrder.value) return "";
    return getMessageByStatus(currentOrder.value.status, currentOrder.value.payment_method);
  });

  const orderStatusClass = computed(() => {
    if (!currentOrder.value) return "";
    return getStatusClass(currentOrder.value.status, currentOrder.value.payment_method);
  });

  const sidebarLinkGroups = computed((): LinkGroup[] => [
    {
      items: [
        {
          id: "information",
          label: "Thông tin tài khoản",
          des: "Cập nhật thông tin tài khoản",
          link: { name: "customer-my-profile" },
          class: "information",
          message: isUpdateProfile.value ? "Cập nhật ngay!" : "",
          classStatus: isUpdateProfile.value ? "profile-usermenu__label--warning" : "",
        },
        {
          id: "my-coupon",
          label: "Mã ưu đãi",
          des: "Quản lý mã dành riêng cho bạn",
          link: { name: "customer-my-coupon" },
          class: "coupon",
          message: voucherData.value?.result?.countExiringVouchers > 0 ? `${voucherData.value?.result?.countExiringVouchers} mã sắp hết hạn` : "",
          count: voucherData.value?.result?.vouchers?.length > 0 ? voucherData.value?.result?.vouchers?.length : 0,
          classStatus: voucherData.value?.result?.countExiringVouchers > 0 ? "profile-usermenu__label--warning" : "",
        },
        {
          id: "order-history",
          label: "Đơn hàng",
          des: "Tra cứu thông tin vận chuyển",
          link: { name: "customer-order-history" },
          class: "order",
          message: orderStatusMessage.value,
          classStatus: orderStatusClass.value,
        },
        {
          id: "membership-card",
          label: "Thẻ thành viên",
          des: "Thông tin hội viên và lịch sử tích điểm",
          link: { name: "customer-membership-card" },
          class: "membership",
        },
        {
          id: "address-details",
          label: "Sổ địa chỉ",
          des: "Cập nhật địa chỉ giao hàng",
          link: { name: "customer-addresses-details" },
          class: "address",
        },
        // {
        //   id: "my-recently-viewed",
        //   label: "My Recently Viewed",
        //   des: "",
        //   link: { name: "customer-my-recently-viewed" },
        //   class: "viewed",
        // },
        {
          id: "log-out",
          label: "Đăng xuất",
          class: "logout",
        },
      ],
    },
  ]);
  const sidebarLinkGroupsMobile = computed((): LinkGroup[] => [
    {
      items: [
        // {
        //   id: 'my-reviews',
        //   label: 'My reviews',
        //   link: { name: 'customer-my-reviews' },
        // },
        // {
        //   id: 'my-newsletter',
        //   label: 'My newsletter',
        //   link: { name: 'customer-my-newsletter' },
        // },
        // {
        //   id: 'my-profile',
        //   label: 'My profile',
        //   link: { name: 'customer-my-profile' },
        // },
        {
          id: "my-coupon",
          label: "Coupon Code",
          link: { name: "customer-my-coupon" },
          class: "coupon",
          count: voucherData.value?.result?.total > 0 ? voucherData.value?.result?.total : null,
        },
        {
          id: "order-history",
          label: "Order History",
          link: { name: "customer-order-history" },
          class: "order",
        },
        {
          id: "membership-card",
          label: "Membership card",
          link: { name: "customer-membership-card" },
          class: "membership",
        },
        {
          id: "address-details",
          label: "Address Details",
          link: { name: "customer-addresses-details" },
          class: "address",
        },
        {
          id: "feedback-link",
          label: "Account Feedback",
          class: "feedback",
        },
        {
          id: "account-support",
          label: "Account Support",
          class: "support",
        },
        {
          id: "canifa-blog",
          label: "Canifalife",
          class: "blog",
        },
      ],
    },
  ]);

  const logoutUser = async () => {
    await logout({});
    await clear({});
    await router.push(localeRoute({ name: "home" }));
  };

  const openZaloChat = () => {
    if (typeof window !== "undefined") {
      window.open("https://zalo.me/2576180316648412566", "_blank");
    }
  };

  const openCanifalife = () => {
    if (typeof window !== "undefined") {
      window.open("https://canifa.com/blog/", "_blank");
    }
  };

  const goToSupport = async () => {
    await router.push("/hoi-dap");
    toggleAccounMobile(false);
  };

  const getMessageByStatus = (status, payment_method) => {
    if (!status) return "";
    if (!payment_method) return "";
    switch (status) {
      case "Created":
        return "Đang xử lý";
      case "Validated":
        return "Đang xử lý";
      case "To be Prepared":
        return "Đang vận chuyển";
      case "Prepared":
        return "Đang vận chuyển";
      case "Package given to carrier":
        return "Đã giao";
      case "complete":
        return "Đã giao";
      case "Cancel":
        return "Đã hủy";
      case "Pending":
        if (payment_method !== "cashondelivery") {
          return "Chờ thanh toán";
        } else {
          return "Đang xử lý";
        }
      default:
        return "";
    }
  };

  const getStatusClass = (status, payment_method) => {
    if (!status) return "";
    switch (status) {
      case "Created":
        return "profile-usermenu__label--processing";
      case "Validated":
      case "To be Prepared":
        return "profile-usermenu__label--processing";
      case "Prepared":
        return "profile-usermenu__label--shipping";
      case "Package given to carrier":
      case "complete":
        return "profile-usermenu__label--delivered";
      case "Cancel":
        return "profile-usermenu__label--cancel";
      case "Pending":
        if (payment_method !== "cashondelivery") {
          return "profile-usermenu__label--waiting";
        } else {
          return "profile-usermenu__label--processing";
        }
      default:
        return "";
    }
  };

  return {
    sidebarLinkGroups,
    sidebarLinkGroupsMobile,
    logoutUser,
    openZaloChat,
    openCanifalife,
    goToSupport,
    authRequiredPages,
  };
};
